import axios from "axios";
import router from "../router";
import store from "../store/index";
import Login from "@/services/login";
import Vue from "vue";

let logoutPromise = null;
let axiosInstance = null;
let abortController = new AbortController();

const getAxiosInstance = () => {
  if (axiosInstance) {
    return axiosInstance;
  }

  axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_BASE_URL
      ? process.env.VUE_APP_BASE_URL
      : "http://" + location.hostname + ":3030",
    timeout: 30000
  });

  // LOGOUT WHEN TOKEN EXPIRES
  const logoutProcess = async (path) => {
    // If there's already an ongoing logout process, return that Promise
    if (logoutPromise) {
      return logoutPromise;
    }

    // Create a new logout promise to ensure only one logout occurs
    logoutPromise = new Promise(async (resolve, reject) => {
      const token = store.getters.getGeneralState("token");
      const { email, userId } = store.getters.getGeneralState("userData");

      // Logout logic
      try {
        if (token) {
          const payload = { token, userId, email };
          await Login.logoutUser(payload);  // Logout API call
        }
        store.dispatch("resetState");
        localStorage.clear();
        await router.push(path);
        resolve();
      } catch (err) {
        console.error("Logout error:", err);
        reject(err);
      } finally {
        const theme = [
          { key: 'bg', value: "#04112F" },
          { key: 'primary', value: "#000427" },
          { key: 'secondary', value: "#3D485D" },
          { key: 'primaryText', value: "#ffffff" },
          { key: 'sixth', value: "#121a37" },
          { key: 'extraGrey', value: "#D4D4D4" },
          { key: 'number', value: "#668cf6" }
        ];
        theme.forEach(el => {
          Vue.prototype.$vuetify.framework.theme[el.key] = el.value;
        });
        logoutPromise = null;
      }
    });

    return logoutPromise;
  };

  axiosInstance.interceptors.request.use(
    (request) => {
      const token = store.getters.getGeneralState("token");
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response) {
        let payload = { icon: "error" };
        const status = error.response.status;

        // Handle 403 Forbidden - Session expired
        if (status === 403) {
          try {
            cancelAllRequests();
            await logoutProcess("/login");
            payload.message = "Session has expired";
          } catch (err) {
            console.error("Error during logout:", err);
          }
        } else if (error.response.data && error.response.data.message) {
          payload.header = "Error";
          payload.message = error.response.data.message;
        } else if (error.response.data && error.response.data.error) {
          payload.header = "Error";
          payload.message = error.response.data.error;
        } else {
          payload.header = "Unknown Error";
        }
  
        // Prevent showing snackbar for login conflict (409)
        if ((error.response.config.url.includes('/api/v2/verify2FA') && status === 409) || error.response.config.url.includes('/api/v2/send-login-otp')) {
          return Promise.reject(error);
        }

        // Show snackbar notification
        store.commit("SET_SHOW_SNACKBAR", true);
        store.commit("SET_SNACKBAR_CONTENT", payload);
      } else if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
        return Promise.reject(error);
      } else {
        console.error("Axios request error (no response):", error);
        store.commit("SET_SHOW_SNACKBAR", true);
        store.commit("SET_SNACKBAR_CONTENT", {
          icon: "error",
          header: "Network Error",
          message: error.message || "Could not connect to the server."
        });
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const refreshToken = () => {
  abortController = new AbortController();
};

export const cancelAllRequests = (message = "Session expired, canceling all requests.") => {
  abortController.abort(message);
  refreshToken();
};

// Expose API methods with AbortController signal attached
export const Api = () => {
  const apiInstance = getAxiosInstance();

  return {
    post: (...params) => {
      const [url, data, config] = params;
      return apiInstance.post(url, data, {
        ...config,
        signal: abortController.signal
      });
    },
    patch: (...params) => {
      const [url, data, config] = params;
      return apiInstance.patch(url, data, {
        ...config,
        signal: abortController.signal,
      });
    },
    get: (...params) => {
      const [url, config] = params;
      return apiInstance.get(url, {
        ...config,
        signal: abortController.signal,
      });
    },
    delete: (...params) => {
      const [url, config] = params;
      return apiInstance.delete(url, {
        ...config,
        signal: abortController.signal,
      });
    },
    request: (params) => {
      return apiInstance.request({
        ...params,
        signal: abortController.signal,
      });
    },
    put: (...params) => {
      const [url, data, config] = params;
      return apiInstance.put(url, data, {
        ...config,
        signal: abortController.signal,
      });
    },
  };
};
